import { Container, Divider, Typography } from '@cleartrip/ct-design-components'
import { motion } from 'framer-motion'
import React, { memo, useCallback, useMemo, useRef } from 'react'

import {
  FC_RT_CALLOUT_SHADE,
  getSrpHeroTheme,
  isSrpFirstClassCabin,
  PE_RT_CALLOUT_SHADE,
  PE_RT_CALLOUT_SHADE_SELECTED,
  PE_TUPLE_CARD_BORDER_COLOR,
  PE_TUPLE_SELECTED_BORDER_COLOR,
} from '@/constants/flights/premiumEconomy'
import { formatCurrency, isValidPrice } from '@/utils/general'

import { JOURNEY_LEG, type JourneyLeg } from './config'
import { TUPLE_COLORS, TUPLE_HOVER_SHADOW } from './config/tupleTheme'
import type { TuplePriceStrip, TupleRoundTripViewModel } from './model/types'
import AirlineBlock from './ui/AirlineBlock'
import DurationStopsBlock from './ui/DurationStopsBlock'
import OfferCallout from './ui/OfferCallout'
import PriceBlock from './ui/PriceBlock'
import TimeRouteBlock from './ui/TimeRouteBlock'
import TupleActions from './ui/TupleActions'
import TupleMetaRow from './ui/TupleMetaRow'

export interface TupleRoundTripProps {
  card: TupleRoundTripViewModel
  leg: JourneyLeg
  isSelected: boolean
  cabin?: string
  cardIndex?: number
  onSelect?: (card: TupleRoundTripViewModel) => void
  onFlightDetails?: (card: TupleRoundTripViewModel, leg: JourneyLeg) => void
  onStripClick?: (card: TupleRoundTripViewModel) => void
}

const CSS_OUTER = { minWidth: 0 }
const CSS_ROUTE_PRICE = { minWidth: 0, gap: 4 }
const CSS_MIDDLE = { flex: '0 0 312px', width: 312, maxWidth: 312, minWidth: 312, columnGap: 12 }

const resolveStrip = (card: TupleRoundTripViewModel, leg: JourneyLeg): TuplePriceStrip | undefined => {
  if (card.priceStrip && isValidPrice(card.priceStrip.displayPrice)) return card.priceStrip
  if (leg === JOURNEY_LEG.ONWARD && card.lowestRoundTripPrice != null && isValidPrice(card.lowestRoundTripPrice)) {
    return {
      label: 'Lowest roundtrip price',
      displayPrice: card.lowestRoundTripPrice,
      strikeOffPrice: card.lowestRoundTripStrikePrice,
    }
  }
  return undefined
}

const TupleRoundTrip: React.FC<TupleRoundTripProps> = ({
  card,
  leg,
  isSelected,
  cabin,
  cardIndex,
  onSelect,
  onFlightDetails,
  onStripClick,
}) => {
  const heroTheme = getSrpHeroTheme(cabin)
  const isFirstClass = isSrpFirstClassCabin(cabin)

  const strip = resolveStrip(card, leg)
  const lastStripRef = useRef<TuplePriceStrip | undefined>(strip)
  if (strip) lastStripRef.current = strip
  const displayStrip: (Pick<TuplePriceStrip, 'label'> & Partial<TuplePriceStrip>) | undefined =
    strip ?? lastStripRef.current ?? (leg === JOURNEY_LEG.RETURN ? { label: 'Total price' } : undefined)

  const handleStripClick = useCallback(() => onStripClick?.(card), [card, onStripClick])
  const handleSelect = useCallback(() => onSelect?.(card), [card, onSelect])
  const handleDetails = useCallback(() => onFlightDetails?.(card, leg), [card, leg, onFlightDetails])

  const footerColor = heroTheme
    ? isFirstClass
      ? TUPLE_COLORS.white
      : TUPLE_COLORS.heading
    : isSelected
      ? TUPLE_COLORS.white
      : TUPLE_COLORS.heading

  const trailing = useMemo(
    () => (
      <Container className='flex flex-middle flex-wrap flex-end' css={{ columnGap: 0, rowGap: 6 }}>
        <OfferCallout
          cardIndex={cardIndex}
          couponCallout={card.couponCallout}
          fareId={card.fareId}
          showPerAdult={card.price.showPerAdult}
          trailing={
            <Divider
              borderColor={TUPLE_COLORS.metaDivider}
              borderStyle='solid'
              className='flex-no-shrink mx-2'
              height='14px'
              type='solid'
              width='1px'
            />
          }
        />
        <TupleActions variant='details' onFlightDetails={handleDetails} />
      </Container>
    ),
    [card.fareId, card.couponCallout, card.price.showPerAdult, cardIndex, handleDetails]
  )

  const flightBody = (
    <Container className='flex flex-column flex-rg-3 p-3 w-100p' css={CSS_OUTER}>
      <AirlineBlock airlines={card.airlines} density='compact' flightNumbersLabel={card.flightNumbersLabel} />

      <Container className='flex flex-top flex-between w-100p' css={CSS_ROUTE_PRICE}>
        <Container className='flex flex-middle' css={CSS_MIDDLE}>
          <TimeRouteBlock density='compact' endpoint={card.departure} />
          <DurationStopsBlock density='compact' durationLabel={card.durationLabel} stop={card.stop} />
          <TimeRouteBlock align='end' density='compact' endpoint={card.arrival} />
        </Container>
        <PriceBlock density='compact' price={card.price} />
      </Container>

      <TupleMetaRow density='compact' tags={card.tags} trailing={trailing} />
    </Container>
  )

  const priceReady = displayStrip != null && isValidPrice(displayStrip.displayPrice)
  const priceStrip = displayStrip ? (
    <Container
      className='flex flex-middle flex-between px-3 py-2 w-100p'
      role={onStripClick ? 'button' : undefined}
      style={{
        ...(onStripClick ? { cursor: 'pointer' } : undefined),
        transition: 'background-color 0.25s ease, color 0.25s ease',
      }}
      tabIndex={onStripClick ? 0 : undefined}
      onClick={onStripClick ? handleStripClick : undefined}
      onKeyDown={
        onStripClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleStripClick()
              }
            }
          : undefined
      }
    >
      <Typography
        className='ws-nowrap'
        componentNode='span'
        style={{ color: footerColor, transition: 'color 0.25s ease' }}
        variant='B3'
      >
        {displayStrip.label}
      </Typography>
      <Container className='flex flex-baseline flex-cg-1'>
        {priceReady &&
          displayStrip.strikeOffPrice != null &&
          displayStrip.strikeOffPrice > (displayStrip.displayPrice ?? 0) && (
            <Typography
              isStriked
              className='ws-nowrap'
              componentNode='span'
              style={{ color: footerColor, transition: 'color 0.25s ease' }}
              variant='P3'
            >
              {formatCurrency(displayStrip.strikeOffPrice)}
            </Typography>
          )}
        <Typography
          className='ws-nowrap'
          componentNode='span'
          style={{ color: footerColor, transition: 'color 0.25s ease' }}
          variant='HM4'
        >
          {priceReady ? formatCurrency(displayStrip.displayPrice ?? 0) : '\u00a0'}
        </Typography>
      </Container>
    </Container>
  ) : null

  if (heroTheme) {
    const isGold = !isFirstClass
    const peOuterBg = isSelected ? PE_RT_CALLOUT_SHADE_SELECTED : PE_RT_CALLOUT_SHADE
    const fcOuterBg = isSelected
      ? FC_RT_CALLOUT_SHADE || 'linear-gradient(89.91deg, #532805 0%, #372305 99.9%)'
      : `linear-gradient(#532805, #372305) padding-box, ${heroTheme.tupleBorderGradient} border-box`

    return (
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className={`flex flex-column br-16 w-100p ${isGold && !isSelected ? '' : 'o-hidden'}`}
        initial={{ opacity: 0, y: 8 }}
        style={{
          border: isFirstClass
            ? isSelected
              ? `2px solid ${PE_TUPLE_SELECTED_BORDER_COLOR}`
              : '1px solid transparent'
            : isSelected
              ? `2px solid ${PE_TUPLE_SELECTED_BORDER_COLOR}`
              : `1px solid ${TUPLE_COLORS.cardBorder}`,
          background: isFirstClass ? fcOuterBg : peOuterBg,
          boxShadow: isSelected ? '0 4px 14px 0 rgba(0, 0, 0, 0.16)' : 'none',
          transition: 'background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
        }}
        transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
        whileHover={isSelected ? undefined : { boxShadow: TUPLE_HOVER_SHADOW }}
        whileTap={{ scale: 0.997 }}
      >
        <Container
          aria-pressed={isSelected}
          className={`p-relative flex flex-column bg-white bn c-pointer ta-left ${
            isGold && !isSelected ? 'o-hidden' : ''
          }`}
          role='button'
          style={{
            borderRadius: isSelected ? '12px 12px 16px 16px' : '16px',
            ...(isGold && !isSelected
              ? { border: `1px solid ${PE_TUPLE_CARD_BORDER_COLOR}`, margin: '-1px -1px 0' }
              : undefined),
            transition: 'box-shadow 0.2s ease',
          }}
          tabIndex={0}
          onClick={handleSelect}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleSelect()
            }
          }}
        >
          {flightBody}
        </Container>
        {priceStrip}
      </motion.div>
    )
  }

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className='flex flex-column br-16 o-hidden w-100p'
      initial={{ opacity: 0, y: 8 }}
      style={{
        backgroundColor: isSelected ? TUPLE_COLORS.selectedShell : TUPLE_COLORS.unselectedShell,
        border: isSelected ? `2px solid ${TUPLE_COLORS.heading}` : `1px solid ${TUPLE_COLORS.cardBorder}`,
        boxShadow: isSelected ? '0 4px 14px 0 rgba(10, 64, 151, 0.18)' : 'none',
        transition: 'background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
      }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      whileHover={isSelected ? undefined : { boxShadow: TUPLE_HOVER_SHADOW }}
      whileTap={{ scale: 0.997 }}
    >
      <Container
        aria-pressed={isSelected}
        className='p-relative flex flex-column br-12 bg-white o-hidden w-100p bn c-pointer ta-left'
        role='button'
        style={{
          transition: 'box-shadow 0.2s ease',
        }}
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleSelect()
          }
        }}
      >
        {flightBody}
      </Container>
      {priceStrip}
    </motion.div>
  )
}

export default memo(TupleRoundTrip)
